/**
 * AttachExistingBuildingDeals Component
 *
 * Building-specific component for attaching existing deals to a building.
 * Follows the same architecture as AttachExistingDeals but uses building-specific context.
 */

import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Icon, SearchInput, Loading } from '@popety-io/ui'
import { useBuildingDeals } from '../../contexts/BuildingDealContext'
import { apiClient } from '@/services/apiClient'

import {
  Container,
  Header,
  BackButton,
  HeaderTitle,
  FormContainer,
  FormGroup,
  SearchResults,
  SearchResultItem,
  SearchResultInfo,
  SearchResultName,
  SearchResultMeta,
  SearchResultValue,
  FormFooter,
  LoadingContainer,
  EmptyState,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
} from '../../../../../../../Explore/DeveloperMap/Lands/components/PropertyPanel/components/AttachExistingDeals/AttachExistingDeals.styles'
import styled from 'styled-components'

// Additional styled components for building-specific functionality
const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`
const HeaderRight = styled.div``
const Content = styled(FormContainer)``
const SearchSection = styled(FormGroup)``
const SearchContainer = styled.div`
  margin-bottom: 16px;
`
const FilterContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
`
const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  background: #ffffff;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #00ade6;
    box-shadow: 0 0 0 3px rgba(0, 173, 230, 0.1);
  }
`
const DealsSection = styled.div`
  flex: 1;
  overflow-y: auto;
`
const DealsList = styled(SearchResults)``
const DealItem = styled(SearchResultItem)``
const DealCheckbox = styled.input`
  margin-right: 12px;
`
const DealInfo = styled(SearchResultInfo)``
const DealItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`
const DealTitle = styled(SearchResultName)``
const DealMeta = styled(SearchResultMeta)``
const DealStage = styled.span``
const DealValue = styled(SearchResultValue)``
const DealDescription = styled.div`
  font-size: 12px;
  color: #6b6b6b;
  margin-top: 4px;
  line-height: 1.4;
`
const PipelineInfo = styled.div`
  font-size: 12px;
  color: #6b6b6b;
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
`
const PipelineBadge = styled.span`
  background: #f0f9ff;
  color: #0284c7;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 500;
`
const StageBadge = styled.span`
  background: #f0fdf4;
  color: #16a34a;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 500;
`
const InformationsDisplay = styled.div`
  font-size: 12px;
  color: #6b6b6b;
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`
const InfoItem = styled.span`
  background: #f5f5f5;
  padding: 2px 8px;
  border-radius: 4px;
`
const ActionButtons = styled(FormFooter)``
const ErrorMessage = styled.div`
  color: #ff3b30;
  font-size: 14px;
  margin-bottom: 16px;
  padding: 8px 12px;
  background: #ffebeb;
  border-radius: 4px;
`

interface AttachExistingBuildingDealsProps {
  buildingId: string
  onClose: () => void
  onSuccess?: () => void
}

interface Deal {
  id: string
  title: string
  informations?: Record<string, any>
  pipeline_kanban_column_name: string
  pipeline_kanban_name: string
  pipeline_kanban_id: string
  create_timestamp: string
  status?: string
  is_archived?: boolean
}

interface Pipeline {
  id: string
  name: string
}

export const AttachExistingBuildingDeals: React.FC<
  AttachExistingBuildingDealsProps
> = ({ buildingId, onClose, onSuccess }) => {
  const { t } = useTranslation('explore')
  const { linkDealToBuilding } = useBuildingDeals()

  // State
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedPipeline, setSelectedPipeline] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('')
  const [deals, setDeals] = useState<Deal[]>([])
  const [selectedDeals, setSelectedDeals] = useState<Set<string>>(new Set())
  const [pipelines, setPipelines] = useState<Pipeline[]>([])

  // Loading states
  const [loadingDeals, setLoadingDeals] = useState(false)
  const [loadingPipelines, setLoadingPipelines] = useState(true)
  const [attaching, setAttaching] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load pipelines on mount
  useEffect(() => {
    const loadPipelines = async () => {
      try {
        setLoadingPipelines(true)
        const response = await apiClient.get('/pipelines/kanbans')
        setPipelines(response.data || [])
      } catch (err: any) {
        console.error('Error loading pipelines:', err)
        setError(t('propertyPanel.deals.loadPipelinesError'))
      } finally {
        setLoadingPipelines(false)
      }
    }

    loadPipelines()
  }, [])

  // Search deals when filters change
  useEffect(() => {
    const searchDeals = async () => {
      try {
        setLoadingDeals(true)
        setError(null)

        const params: any = {
          limit: 100,
        }

        if (searchQuery.trim()) {
          params.search = searchQuery.trim()
        }

        if (selectedPipeline) {
          params.pipelineId = selectedPipeline
        }

        if (selectedStatus) {
          params.status = selectedStatus
        }

        const response = await apiClient.get('/pipelines/kanbandeals/my-deals')
        const allDeals = response.data || []

        // Filter out deals that are already attached to this building
        const attachedDealsResponse = await apiClient.get(
          `/pipelines/pipelinekanbandealsbuildings/building/${buildingId}`
        )
        const attachedDealIds = new Set(
          (attachedDealsResponse.data || []).map(
            (item: any) => item.pipelineKanbanDealId
          )
        )

        // Filter to show only open (non-archived) deals that are not already attached
        const availableDeals = allDeals.filter(
          (deal: Deal) =>
            !attachedDealIds.has(deal.id) &&
            !deal.is_archived &&
            deal.status === 'open'
        )

        setDeals(availableDeals)
      } catch (err: any) {
        console.error('Error searching deals:', err)
        setError(
          err.response?.data?.message ||
            t('propertyPanel.deals.searchDealsError')
        )
        setDeals([])
      } finally {
        setLoadingDeals(false)
      }
    }

    const timeoutId = setTimeout(searchDeals, 300)
    return () => clearTimeout(timeoutId)
  }, [searchQuery, selectedPipeline, selectedStatus, buildingId])

  const handleDealToggle = (dealId: string) => {
    setSelectedDeals((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(dealId)) {
        newSet.delete(dealId)
      } else {
        newSet.add(dealId)
      }
      return newSet
    })
  }

  const handleSelectAll = () => {
    if (selectedDeals.size === deals.length) {
      setSelectedDeals(new Set())
    } else {
      setSelectedDeals(new Set(deals.map((deal) => deal.id)))
    }
  }

  const handleAttachDeals = async () => {
    if (selectedDeals.size === 0) return

    setAttaching(true)
    setError(null)

    try {
      // Attach each selected deal to the building
      await Promise.all(
        Array.from(selectedDeals).map((dealId) =>
          linkDealToBuilding(dealId, buildingId)
        )
      )

      onSuccess?.()
    } catch (err: any) {
      console.error('Error attaching deals to building:', err)
      setError(
        err.response?.data?.message ||
          t(
            'propertyPanel.deals.attachError',
            'Failed to attach deals. Please try again.'
          )
      )
    } finally {
      setAttaching(false)
    }
  }

  const formatValue = (amount: number | undefined, currency?: string) => {
    if (!amount) return ''
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: currency || 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-CH')
  }

  const filteredDeals = useMemo(() => {
    return deals.filter((deal) => {
      const matchesSearch =
        !searchQuery ||
        deal.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        deal.informations?.description
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase())

      return matchesSearch
    })
  }, [deals, searchQuery])

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={onClose}>
            <Icon name="arrow-left" size="small" />
          </BackButton>
          <HeaderTitle>
            {t(
              'propertyPanel.deals.attachExistingBuilding',
              'Attach Existing Deals to Building'
            )}
          </HeaderTitle>
        </HeaderLeft>
        <HeaderRight />
      </Header>

      <Content>
        <SearchSection>
          <SearchContainer>
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={t(
                'propertyPanel.deals.searchPlaceholder',
                'Search deals by name or description...'
              )}
              clearable
            />
          </SearchContainer>

          <FilterContainer>
            <FilterSelect
              value={selectedPipeline}
              onChange={(e) => setSelectedPipeline(e.target.value)}
              disabled={loadingPipelines}
            >
              <option value="">
                {loadingPipelines
                  ? t('propertyPanel.deals.loadingPipelines', 'Loading...')
                  : t('propertyPanel.deals.allPipelines', 'All Pipelines')}
              </option>
              {pipelines.map((pipeline) => (
                <option key={pipeline.id} value={pipeline.id}>
                  {pipeline.name}
                </option>
              ))}
            </FilterSelect>

            <FilterSelect
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="">
                {t('propertyPanel.deals.allStatuses', 'All Statuses')}
              </option>
              <option value="open">
                {t('propertyPanel.deals.open', 'Open')}
              </option>
              <option value="won">{t('propertyPanel.deals.won', 'Won')}</option>
              <option value="lost">
                {t('propertyPanel.deals.lost', 'Lost')}
              </option>
            </FilterSelect>
          </FilterContainer>
        </SearchSection>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <DealsSection>
          {loadingDeals ? (
            <LoadingContainer>
              <Loading size="large" />
            </LoadingContainer>
          ) : filteredDeals.length === 0 ? (
            <EmptyState>
              <EmptyIcon>
                <Icon name="real-estate-listing" size="large" color="#d1d5db" />
              </EmptyIcon>
              <EmptyTitle>
                {t('propertyPanel.deals.noDealsFound', 'No deals found')}
              </EmptyTitle>
              <EmptyDescription>
                {searchQuery || selectedPipeline || selectedStatus
                  ? t(
                      'propertyPanel.deals.noDealsFoundFiltered',
                      'No deals match your search criteria. Try adjusting your filters.'
                    )
                  : t(
                      'propertyPanel.deals.noDealsAvailable',
                      'No deals are available to attach to this building.'
                    )}
              </EmptyDescription>
            </EmptyState>
          ) : (
            <>
              <div
                style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid #e5e5e5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '14px',
                  color: '#6b6b6b',
                }}
              >
                <span>
                  {t(
                    'propertyPanel.deals.foundDeals',
                    '{{count}} deals found',
                    {
                      count: filteredDeals.length,
                    }
                  )}
                  {selectedDeals.size > 0 &&
                    ` • ${selectedDeals.size} ${t('propertyPanel.deals.selected', 'selected')}`}
                </span>
                <Button
                  variant="ghost"
                  size="small"
                  onClick={handleSelectAll}
                  disabled={filteredDeals.length === 0}
                >
                  {selectedDeals.size === filteredDeals.length
                    ? t('propertyPanel.deals.selectNone', 'Select None')
                    : t('propertyPanel.deals.selectAll', 'Select All')}
                </Button>
              </div>

              <DealsList>
                {filteredDeals.map((deal) => (
                  <DealItem
                    key={deal.id}
                    $selected={selectedDeals.has(deal.id)}
                    onClick={() => handleDealToggle(deal.id)}
                  >
                    <DealCheckbox
                      type="checkbox"
                      checked={selectedDeals.has(deal.id)}
                      onChange={() => {}} // Handled by onClick
                    />

                    <DealInfo>
                      <DealItemHeader>
                        <DealTitle>
                          {deal.title || t('propertyPanel.deals.untitledDeal')}
                        </DealTitle>
                        <DealValue>
                          {formatValue(
                            deal.informations?.amount,
                            deal.informations?.currency
                          )}
                        </DealValue>
                      </DealItemHeader>

                      <PipelineInfo>
                        <PipelineBadge>
                          {deal.pipeline_kanban_name ||
                            t('propertyPanel.deals.noPipeline')}
                        </PipelineBadge>
                        <span>→</span>
                        <StageBadge>
                          {deal.pipeline_kanban_column_name ||
                            t('propertyPanel.deals.noStage')}
                        </StageBadge>
                      </PipelineInfo>

                      <DealMeta>
                        <span>{formatDate(deal.create_timestamp)}</span>
                      </DealMeta>

                      {deal.informations?.description && (
                        <DealDescription>
                          {deal.informations.description}
                        </DealDescription>
                      )}

                      {deal.informations &&
                        Object.keys(deal.informations).length > 0 && (
                          <InformationsDisplay>
                            {Object.entries(deal.informations)
                              .filter(
                                ([key]) =>
                                  ![
                                    'amount',
                                    'currency',
                                    'description',
                                    'summary',
                                  ].includes(key)
                              )
                              .slice(0, 3)
                              .map(([key, value]) => (
                                <InfoItem key={key}>
                                  {key.replace(/_/g, ' ')}: {String(value)}
                                </InfoItem>
                              ))}
                          </InformationsDisplay>
                        )}
                    </DealInfo>
                  </DealItem>
                ))}
              </DealsList>
            </>
          )}
        </DealsSection>

        <ActionButtons>
          <Button
            variant="secondary"
            size="medium"
            onClick={onClose}
            disabled={attaching}
          >
            {t('propertyPanel.deals.cancel', 'Cancel')}
          </Button>
          <Button
            variant="primary"
            size="medium"
            onClick={handleAttachDeals}
            disabled={selectedDeals.size === 0 || attaching}
          >
            {attaching && <Loading size="small" />}
            {t('propertyPanel.deals.attachSelected', 'Attach Selected')} (
            {selectedDeals.size})
          </Button>
        </ActionButtons>
      </Content>
    </Container>
  )
}
